/**
 * Reconciles a stored `columnOrder` array against the current `columns`
 * definition. Two guarantees:
 *
 *  1. Columns marked `meta.disableManagement === true` (e.g. a row-selection
 *     checkbox column the user can't toggle / reorder) keep their position
 *     from the source `columns` array — they're never displaced even if a
 *     persisted user/board order skipped them or moved them.
 *  2. Manageable columns persisted in `storedOrder` are placed in the order
 *     the user persisted; any manageable columns missing from `storedOrder`
 *     (e.g. brand-new columns added after the user saved their order) are
 *     appended after the persisted ones, in their original `columns` order.
 *
 * Result: every id from `columns` appears exactly once.
 */
export type ColumnIdSpec = {
  id: string;
  isFixed: boolean;
};

export const reconcileColumnOrder = (
  storedOrder: ReadonlyArray<string>,
  columns: ReadonlyArray<ColumnIdSpec>,
): string[] => {
  const availableIds = columns.map(c => c.id);
  const fixedIds = new Set(columns.filter(c => c.isFixed).map(c => c.id));
  const manageableIds = columns.filter(c => !c.isFixed).map(c => c.id);
  const available = new Set(availableIds);

  // Compute the manageable section first: stored order (filtered to known
  // ids) plus any manageable columns the user hasn't persisted yet,
  // appended in their `columns` order.
  const manageableSeen = new Set<string>();
  const manageableOrdered: string[] = [];
  for (const id of storedOrder) {
    if (available.has(id) && !fixedIds.has(id) && !manageableSeen.has(id)) {
      manageableOrdered.push(id);
      manageableSeen.add(id);
    }
  }
  for (const id of manageableIds) {
    if (!manageableSeen.has(id)) {
      manageableOrdered.push(id);
      manageableSeen.add(id);
    }
  }

  // Now walk the original `columns` array and produce the final order:
  // every fixed column keeps its source position; every manageable slot is
  // filled from `manageableOrdered` in sequence.
  const result: string[] = [];
  let manageableCursor = 0;
  for (const { id, isFixed } of columns) {
    if (isFixed) {
      result.push(id);
    } else {
      const nextManageable = manageableOrdered[manageableCursor++];
      if (nextManageable !== undefined) {
        result.push(nextManageable);
      }
    }
  }

  return result;
};
